import { Button } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { UploadIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import type { FileDropzoneProps } from './FileDropzone.types';

/**
 * A drag-and-drop target with a click-to-select fallback, showing what formats it accepts.
 *
 * Extracted from the project-artifact upload flow's `UploadView` so a second upload surface
 * (brand media, fonts) does not hand-roll a second dropzone. Renders only the drop target itself
 * -- a caller's own chip/queue list and any warnings banner stay caller-owned, since those differ
 * between an artifact queue and a font upload.
 */
export function FileDropzone({
  accept,
  maxSize,
  multiple = true,
  disabled = false,
  buttonDisabled = disabled,
  title,
  caption,
  buttonLabel,
  onDrop,
  className,
}: FileDropzoneProps) {
  const wholeAreaClickable = buttonLabel === undefined;

  // With a button, noClick keeps it the sole click trigger so a click anywhere else cannot open a
  // picker nobody asked for. Without one there is nothing else to press, so the area takes the
  // click itself -- and the keyboard with it, which is why it stops being presentational.
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple,
    noClick: !wholeAreaClickable,
    noKeyboard: !wholeAreaClickable,
    disabled,
  });

  return (
    <div
      {...getRootProps(
        wholeAreaClickable
          ? { role: 'button', 'aria-label': title, 'aria-disabled': disabled }
          : {},
      )}
      data-testid="upload-dropzone"
      className={clsx(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center transition-colors',
        disabled && 'cursor-not-allowed border-border bg-muted/30',
        !disabled && isDragActive && 'border-primary bg-primary/5',
        !disabled && !isDragActive && 'border-border',
        !disabled && wholeAreaClickable && 'cursor-pointer hover:border-primary hover:bg-accent/40',
        wholeAreaClickable && 'focus-visible:focus-ring outline-none',
        className,
      )}
    >
      <input {...getInputProps()} />
      <div className="rounded-md bg-accent p-2">
        <UploadIcon className="size-5 text-primary" />
      </div>
      <span className="text-base font-medium">{title}</span>
      <span className="text-sm text-muted-foreground">{caption}</span>
      {buttonLabel !== undefined && (
        <Button size="default" disabled={buttonDisabled} onClick={open}>
          {buttonLabel}
        </Button>
      )}
    </div>
  );
}
